<template>
  <div class="enrollments container py-4">
    <!-- Alert -->
    <div v-if="alert.message" class="mb-3">
      <div
        :class="[
          'alert',
          alert.type === 'success' ? 'alert-success' : 'alert-danger',
        ]"
      >
        {{ alert.message }}
      </div>
    </div>

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="text-pink">Student Topic Enrollments</h2>

      <div class="row g-3 mb-4">
        <div class="col-md-3">
          <select
            class="form-select"
            v-model="selectedGrade"
            @change="onGradeChange"
          >
            <option value="">Select Grade</option>
            <option v-for="g in grades" :key="g.id" :value="g.id">
              {{ g.level }}
            </option>
          </select>
        </div>

        <div class="col-md-6">
          <select
            class="form-select"
            v-model="selectedSubject"
            @change="onSubjectChange"
            :disabled="!selectedGrade"
          >
            <option value="">Select Subject</option>
            <option v-for="s in subjects" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
        </div>

        <div class="col-md-3">
          <select
            class="form-select"
            v-model="selectedPayment"
            @change="fetchStudents"
          >
            <option value="">All Payments</option>
            <option value="paid">Paid</option>
            <option value="not_paid">Not Paid</option>
          </select>
        </div>
      </div>

      <button
        class="btn btn-pink"
        data-bs-toggle="modal"
        data-bs-target="#enrollModal"
      >
        <i class="bi bi-plus-lg"></i> Add Enrollment
      </button>
    </div>

    <!-- Search -->
    <!-- <div class="mb-3">
      <input
        type="text"
        v-model="search"
        class="form-control"
        placeholder="Search by student, grade, subject, or topic..."
      />
    </div> -->

    <p class="text-end text-pink mb-2">{{ totalEnrollments }} Enrollments</p>

    <!-- Table -->
    <div class="table-responsive mt-3">
      <table class="table table-dark table-hover align-middle">
        <thead class="text-pink">
          <tr>
            <th>Student Name</th>
            <th>Payment Status</th>
            <th>Payment Date</th>
            <th>Grade</th>
            <th>Subject</th>
            <th>Enroll Topics</th>
            <th>Valid Until</th>
          </tr>
        </thead>

        <tbody>
          <!-- Loading -->
          <tr v-if="loading">
            <td :colspan="7" class="text-center py-4">
              <div class="spinner-border text-pink"></div>
              <p class="mt-2 text-pink mb-0">Loading enrollments...</p>
            </td>
          </tr>

          <!-- Data -->
          <tr v-else v-for="(row, index) in filteredEnrollments" :key="index">
            <td>{{ row.student_name }}</td>

            <!-- Payment Status -->
            <td>
              <span
                class="badge"
                :class="{
                  'status-active':
                    row.payment_status === 'completed' &&
                    row.valid_until &&
                    new Date(row.valid_until) >= new Date(),

                  'bg-danger':
                    row.payment_status === 'failed' ||
                    !row.payment_status ||
                    (row.valid_until && new Date(row.valid_until) < new Date()),

                  'bg-warning text-dark': row.payment_status === 'pending',
                }"
              >
                {{
                  !row.payment_status
                    ? "Not Paid"
                    : row.valid_until && new Date(row.valid_until) < new Date()
                    ? "Expired"
                    : row.payment_status
                }}
              </span>
            </td>

            <!-- Payment Date -->
            <td>
              {{
                row.payment_date
                  ? new Date(row.payment_date).toLocaleDateString()
                  : "No Payment"
              }}
            </td>

            <td>{{ row.grade }}</td>
            <td>{{ row.subject }}</td>

            <!-- View Topics -->
            <td>
              <button
                class="btn btn-sm btn-info"
                @click="viewTopics(row)"
                data-bs-toggle="modal"
                data-bs-target="#viewEnrollTopicsModal"
              >
                View Topics
              </button>
            </td>

            <!-- Valid Until -->
            <td>
              {{
                row.valid_until
                  ? new Date(row.valid_until).toLocaleDateString()
                  : "-"
              }}
            </td>
          </tr>

          <!-- No Data -->
          <tr v-if="!loading && enrollments.length === 0">
            <td :colspan="7" class="text-center py-4 text-pink">
              <i class="bi bi-person-x fs-2"></i>
              <div class="mt-2">No Enrollments Yet.</div>
              <small>Click "Add Enrollment" to enroll a student.</small>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-center mt-4">
        <nav v-if="lastPage > 1" class="pagination">
          <ul class="pagination custom-pagination">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="fetchStudents(currentPage - 1)">
                Prev
              </button>
            </li>

            <li
              v-for="page in lastPage"
              :key="page"
              class="page-item"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="fetchStudents(page)">
                {{ page }}
              </button>
            </li>

            <li
              class="page-item"
              :class="{ disabled: currentPage === lastPage }"
            >
              <button class="page-link" @click="fetchStudents(currentPage + 1)">
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- View Modal -->
    <div class="modal fade" id="viewEnrollTopicsModal">
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title">
              Topics for {{ selectedStudent?.student_name }}
            </h5>
            <button
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <ViewEnrollmentTopics :student="selectedStudent" />
        </div>
      </div>
    </div>

    <!-- Add Modal -->
    <div class="modal fade" id="enrollModal">
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title">Enroll Student To Topic</h5>
            <button
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <AddTopicEnrollment @topic-enrollment-added="onEnrollmentAdded" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AddTopicEnrollment from "./EnrollmentParts/AddTopicEnrollment.vue";
import ViewEnrollmentTopics from "./EnrollmentParts/ViewEnrollmentTopics.vue";

export default {
  name: "EnrollmentsView",
  components: { AddTopicEnrollment, ViewEnrollmentTopics },

  data() {
    return {
      grades: [],
      subjects: [],
      enrollments: [],
      loading: false,
      selectedGrade: "",
      selectedSubject: "",
      selectedPayment: "",
      search: "",
      alert: { type: "", message: "" },
      selectedStudent: null,

      // ✅ pagination
      currentPage: 1,
      lastPage: 1,
      totalEnrollments: 0,
    };
  },

  computed: {
    filteredEnrollments() {
      if (!this.search) return this.enrollments;

      const term = this.search.toLowerCase();

      return this.enrollments.filter((e) => {
        const topicMatch = e.topics?.some((t) =>
          t.title.toLowerCase().includes(term),
        );

        return (
          e.student_name.toLowerCase().includes(term) ||
          e.grade.toLowerCase().includes(term) ||
          e.subject.toLowerCase().includes(term) ||
          topicMatch
        );
      });
    },
  },

  methods: {
    async loadGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async loadSubjects(gradeId) {
      try {
        const res = await axios.get(`/a-grade-subjects?grade_id=${gradeId}`);
        this.subjects =
          res.data.subjects ||
          res.data.map((s) => ({ id: s.subject.id, name: s.subject.name }));
      } catch (err) {
        console.error(err);
        this.subjects = [];
      }
    },

    async onGradeChange() {
      this.selectedSubject = "";
      await this.loadSubjects(this.selectedGrade);
      await this.fetchStudents();
    },

    async onSubjectChange() {
      await this.fetchStudents();
    },

    viewTopics(student) {
      this.selectedStudent = student; // sets the student to pass to modal
    },
    async fetchStudents(page = 1) {
      this.loading = true;

      try {
        const res = await axios.get("/a-get-topic-enrollments", {
          params: {
            grade_id: this.selectedGrade,
            subject_id: this.selectedSubject,
            payment: this.selectedPayment,
            page: page,
          },
        });

        this.enrollments = res.data.data;
        this.currentPage = res.data.current_page;
        this.lastPage = res.data.last_page;
        this.totalEnrollments = res.data.total;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    onEnrollmentAdded() {
      // Reload fresh data from backend
      this.fetchStudents();

      // Show success alert
      this.alert = {
        type: "success",
        message: "Enrollment added successfully!",
      };

      setTimeout(() => (this.alert.message = ""), 3000);
    },
  },

  mounted() {
    this.loadGrades();
    this.fetchStudents();
  },
};
</script>

<style scoped>
.text-pink {
  color: #ff00cc !important;
}

.btn-pink {
  background-color: #ff00cc;
  color: white;
  border: none;
}
.btn-pink:hover {
  background-color: #e600b3;
  color: white;
}

.border-pink {
  border-color: #ff00cc !important;
}
.border-bottom-pink {
  border-bottom: 2px solid #ff00cc !important;
}

.status-active {
  background: #ff00cc;
  color: white;
  padding: 5px 10px;
  font-size: 12px;
}

.table tbody tr:hover {
  background: #fff0f6;
}

/* Pagination Theme */
.custom-pagination .page-link {
  background-color: #1a1a1a;
  color: #ff00cc;
  border: 1px solid #333;
  transition: 0.3s;
}

.custom-pagination .page-link:hover {
  background-color: #ff00cc;
  color: #fff;
  border-color: #ff00cc;
}

/* Active Page */
.custom-pagination .page-item.active .page-link {
  background-color: #ff00cc;
  border-color: #ff00cc;
  color: white;
  font-weight: bold;
}

/* Disabled */
.custom-pagination .page-item.disabled .page-link {
  background-color: #111;
  color: #666;
  border-color: #222;
  cursor: not-allowed;
}
</style>
